import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'

import {
  getAuthenticatedDestination,
  useAuth,
  usePermissions,
  type Permission,
} from '@/features/auth'

import { ROUTES } from './routes'

interface ProtectedRouteProps {
  children: ReactNode
  /** Permiso necesario para entrar. Sin él, basta con estar autenticado. */
  permission?: Permission
}

/**
 * =============================================================================
 * RUTA PROTEGIDA
 * =============================================================================
 * Envuelve las páginas que requieren sesión y, opcionalmente, un permiso.
 *
 * Hay dos comprobaciones distintas y es importante no confundirlas:
 *
 *   AUTENTICACIÓN (¿quién eres?)  → sin sesión, va al login
 *   AUTORIZACIÓN  (¿qué puedes?)  → con sesión pero sin permiso, va a
 *                                    "sin permiso"
 *
 * Mandar al login a alguien que YA inició sesión sería desconcertante: pensaría
 * que su sesión se cayó, cuando en realidad simplemente no tiene acceso a esa
 * sección. Distinguir los dos casos es lo que hace que la app se sienta
 * confiable.
 *
 * ⚠️ Esto protege la NAVEGACIÓN, no los DATOS. Cualquiera puede modificar el
 * JavaScript que corre en su navegador y forzar la vista. La única protección
 * real está en el backend, que debe rechazar la petición. Ver docs/04-permisos.md
 * =============================================================================
 */
export function ProtectedRoute({ children, permission }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuth()
  const { can, role } = usePermissions()
  const location = useLocation()

  if (!isAuthenticated) {
    return (
      <Navigate
        to={ROUTES.LOGIN}
        // Se recuerda a dónde quería ir para llevarlo ahí tras el login.
        // `replace` evita que el botón "atrás" lo devuelva a una página a la
        // que no tenía acceso.
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  if (user?.mustChangePassword && location.pathname !== ROUTES.REQUIRED_PASSWORD) {
    return <Navigate to={ROUTES.REQUIRED_PASSWORD} replace />
  }

  if (!user?.mustChangePassword && location.pathname === ROUTES.REQUIRED_PASSWORD) {
    return <Navigate to={getAuthenticatedDestination(role, undefined)} replace />
  }

  if (permission !== undefined && !can(permission)) {
    const fallback = getAuthenticatedDestination(role, location.pathname)
    return <Navigate to={fallback === location.pathname ? ROUTES.FORBIDDEN : fallback} replace />
  }

  return <>{children}</>
}
